import React, { useRef, useImperativeHandle, useEffect } from 'react';

import { AnalyticsProperties } from './AnalyticsClient';
import { useAnalytics } from './ReactModule';

export interface ClickTrackingProps {
  eventName: string;
  eventProperties?: AnalyticsProperties;
}

function useRefTracking(
  { eventName, eventProperties = {} }: ClickTrackingProps,
  ref: ((instance: unknown) => void) | React.MutableRefObject<unknown> | null,
) {
  const analytics = useAnalytics();
  const internalRef = useRef<HTMLElement>(null);
  useImperativeHandle(ref, () => internalRef.current);

  useEffect(() => {
    const el = internalRef.current;
    if (!el) {
      return;
    }

    const handler = (e: MouseEvent) => {
      const button_copy =
        e.currentTarget instanceof HTMLElement
          ? e.currentTarget.innerText ?? undefined
          : undefined;
      analytics.trackClick(eventName, {
        button_copy,
        ...eventProperties,
      });
    };

    el.addEventListener('click', handler);

    return () => {
      el.removeEventListener('click', handler);
    };
  });

  return internalRef;
}

export const TrackClick = React.forwardRef<
  unknown,
  ClickTrackingProps & { children: React.ReactNode }
>(({ children, ...trackingProps }, ref) => {
  const internalRef = useRefTracking(trackingProps, ref);

  // React.Children.only(children) accomplishes this same thing;
  // however, TypeScript does not know it guards because it throws
  // an error if it fails and so it doesn't properly narrow the scope
  // of the type of the value it returns.
  if (React.Children.count(children) > 1 || !React.isValidElement(children)) {
    if (process.env.NODE_ENV === 'production') {
      return <>{children}</>;
    }

    throw new Error(
      'Only one child is supported within the TrackClick component',
    );
  }

  return React.cloneElement(children, { ref: internalRef });
});

export function withClickTracking<P extends object>(
  Component: React.ComponentType<P>,
) {
  return React.forwardRef(
    ({ eventName, eventProperties, ...rest }: ClickTrackingProps & P, ref) => {
      const internalRef = useRefTracking({ eventName, eventProperties }, ref);

      return <Component {...(rest as P)} ref={internalRef} />;
    },
  );
}
